import { json, type LoaderFunctionArgs } from "@remix-run/node"
import { Link, useLoaderData } from "@remix-run/react"
import { Public } from "~/utils/remix"
import videoDefault from "~/assets/videos/default.webp"
import { getImage } from "~/utils/image.server"
import showdown from "showdown"
import Typography from "~/components/typography"
import { Separator } from "~/components/ui/separator"
import dayjs from "dayjs"
import { useState } from "react"
import { InfiniteScroll } from "~/components/pagination"
import { FindStar, StarPosts } from "~/models/users.server"
import { Button } from "~/components/ui/button"

export const loader = Public(async ({ request, context, params }: LoaderFunctionArgs) => {
  if (!params.username) {
    throw json({ user: null }, { status: 404 })
  }

  const star = await FindStar(context, params.username)
  if (!star) {
    throw json({ user: null }, { status: 404 })
  }

  const url = new URL(request.url)
  let page = url.searchParams.get("page") || "1"

  const posts = await StarPosts(context, star.UserID, parseInt(page))

  return json({
    starName: star.UserName,
    starCommunity: !context.user && star.Private === 2,
    items: posts.map((post) => ({
      SocialPostID: post.SocialPostID,
      UserID: post.UserID,
      Title: post.Title && unescapeHTML(decodeURI(post.Title)),
      Comment: post.Comment && unescapeHTML(decodeURI(post.Comment)),
      Image: getImage(post.File, videoDefault),
      Status: post.Status,
      CreatedAt: post.CreatedAt,
      PublishedAt: post.PublishedAt,
    })),
  })
})

export default function StarSocial() {
  const data = useLoaderData<typeof loader>()
  const [items, setItems] = useState(data.items)
  const converter = new showdown.Converter()

  return (
    <div className="mt-8 mx-auto space-y-8 max-w-lg md:max-w-2xl">
      {data.starCommunity ?
        <div className="text-center p-8">
          <Typography>
            <p>This MakeLoveNotPornstar has chosen to only make their social posts discoverable by the community.</p>
          </Typography>
          <br />
          <br />
          <Link to={`/register`}>
            <Button size="lg">Join here</Button>
          </Link>
        </div>
      : <InfiniteScroll items={items} setItems={setItems} action={`/makelovenotpornstars/${data.starName}/social`}>
          {items &&
            items.map((post, index) => (
              <div key={index}>
                <div key={index} className="md:flex md:justify-start md:items-start md:gap-8">
                  <div className="mx-auto md:m-0 md:flex-none h-80 aspect-square">
                    <img className="h-80 aspect-square object-cover rounded-sm shadow-lg" src={post.Image} />
                  </div>
                  <div className="mt-4 mx-4 md:mt-0 md:mx-0">
                    <Typography>
                      <h2>{post.Title}</h2>
                    </Typography>
                    <div className="mt-1 mb-4">
                      <Typography className="text-xs text-muted-foreground">
                        <p>
                          Posted on:{" "}
                          {dayjs(post.PublishedAt ? post.PublishedAt : post.CreatedAt).format("MM/DD/YYYY hh:mm A")}
                        </p>
                      </Typography>
                      {post.Status == 0 && (
                        <Typography className="inline-block prose-sm py-0.5 px-1.5 my-0 text-xs bg-amber-200 dark:bg-yellow-500 text-foreground dark:text-background rounded-sm italic">
                          Thanks for your post! Hang tight while our curation team reviews it
                        </Typography>
                      )}
                    </div>
                    <Typography>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: converter.makeHtml(post.Comment || ""),
                        }}></div>
                    </Typography>
                  </div>
                </div>
                <Separator className="m-4" />
              </div>
            ))}
        </InfiniteScroll>
      }
    </div>
  )
}

var htmlEntities: any = {
  nbsp: " ",
  cent: "¢",
  pound: "£",
  yen: "¥",
  euro: "€",
  copy: "©",
  reg: "®",
  lt: "<",
  gt: ">",
  quot: '"',
  amp: "&",
  apos: "'",
}

function unescapeHTML(str: string) {
  return str.replace(/\&([^;]+);/g, function (entity, entityCode) {
    var match

    if (entityCode in htmlEntities) {
      return htmlEntities[entityCode]
    } else if ((match = entityCode.match(/^#x([\da-fA-F]+)$/))) {
      return String.fromCharCode(parseInt(match[1], 16))
    } else if ((match = entityCode.match(/^#(\d+)$/))) {
      return String.fromCharCode(~~match[1])
    } else {
      return entity
    }
  })
}
